/* ============================================================
 * indexed-tab-nav.js
 * ------------------------------------------------------------
 * 최초 작성일: 2026-08-31
 *
 * 통이미지 탭 네비게이션 (클릭 스크롤 + 상단 고정 + 스크롤스파이)
 *
 *   · 버튼 클릭 → 같은 순번(index) 패널로 부드럽게 스크롤
 *   · 스크롤 시 탭바가 헤더 하단에 고정(is-fixed), placeholder로 점프 방지
 *   · 스크롤스파이(현재 위치 패널에 맞는 버튼 자동 active)
 *   · 마지막 패널 구간을 지나면 고정 해제 → 다음 섹션을 덮지 않음
 *
 * [스코프] 최상위 .indexed-tab-nav 컨테이너 단위로 동작.
 *   컨테이너 "안에서만" .tab-bar / .tab-panel 을 조회하므로,
 *   안쪽 클래스는 범용 이름 그대로여도 다른 영역과 충돌하지 않음.
 *   한 페이지에 .indexed-tab-nav 가 여러 개면 각각 독립 초기화됨(멀티 인스턴스).
 *
 * [매칭 규칙] 버튼과 패널은 "DOM 등장 순서(index)"로 자동 연결.
 *   버튼 N번째 ↔ 패널 N번째. 개수·순서만 맞추면 됨. data-* 속성 불필요.
 *
 * [두 가지 모드]
 *   1) 기본 모드: 버튼이 부모 폭을 균등 분할(flex). 개수 많으면 버튼이 좁아짐.
 *   2) 스와이프 모드: 컨테이너에 data-swipe 부여 시 활성화.
 *      - 버튼 폭 고정, 넘치면 좌우 스와이프(Swiper 8)
 *      - 스크롤스파이로 활성화된 버튼이 자동으로 가운데 정렬(slideTo)
 *      - Swiper 마크업(.swiper-wrapper/.swiper-slide)은 JS가 런타임에 자동 래핑
 *        → 팀원은 기본 모드와 동일한 마크업 + data-swipe 만 추가하면 됨
 *      - Swiper 전역(window.Swiper)이 없으면 기본 모드로 폴백
 *      - 버튼 너비: data-tab-width(px) 로 지정 가능. 없으면 CSS 값 사용.
			- 예) <div class="indexed-tab-nav" data-swipe data-tab-width="80">
 *
 * [필요 마크업]
 *   .indexed-tab-nav (스와이프 모드는 여기에 data-swipe 속성 추가)
 *     .tab-bar-area > .tab-bar > .tab-bar__btn(a) * N
 *     .tab-panel * N (버튼 개수와 동일)
 *   버튼·패널 모두 .indexed-tab-nav 자식으로 들어가야 스코프가 잡힘.
 *   첫 버튼에 .is-active, 통이미지 버튼은 .tab-bar__img--off / --on 이미지 쌍.
 *   (자세한 마크업·CSS 템플릿은 팀 공지 가이드 참고)
 *
 * [의존성] 기본 모드: 없음(vanilla). 스와이프 모드: Swiper 8 (테마에 이미 로드됨).
 *
 * KR(Cafe24) 원본에서 스킨 헤더 연동 로직을 제거한 해외(Shopify) 버전.
 * 헤더 높이는 <header data-section-type="header"> 요소에서 직접 측정.
 * ============================================================ */
(function () {
	"use strict";

	/* 고정 헤더 하단선: sticky 헤더 요소의 현재 화면상 bottom 값을 직접 측정.
	 * Stiletto 헤더는 <header data-section-type="header"> 요소.
	 * getBoundingClientRect().bottom → 헤더가 화면에 떠 있으면 양수(그 높이),
	 * 스크롤로 헤더가 사라지면 음수 → 0으로 클램프.
	 * 헤더 없는 페이지에선 요소가 없어 0 → 안전.
	 * (헤더는 페이지 전역 1개이므로 인스턴스 공통으로 한 번만 조회) */
	const header = document.querySelector('header[data-section-type="header"]');
	const getHeaderHeight = () => {
		if (!header) return 0;
		const bottom = header.getBoundingClientRect().bottom;
		return bottom > 0 ? bottom : 0;
	};

	/* 컨테이너(root) 1개를 받아 그 안에서만 탭 네비게이션을 초기화 */
	const initTabNav = (root) => {
		if (root.dataset.tabNavReady) return; // 중복 초기화 방지
		const area = root.querySelector(".tab-bar-area");
		const bar = root.querySelector(".tab-bar");
		if (!area || !bar) return;

		const btns = [...root.querySelectorAll(".tab-bar__btn")];
		const panels = [...root.querySelectorAll(".tab-panel")];
		if (btns.length === 0 || panels.length === 0) return;

		root.dataset.tabNavReady = "1";

		/* ===== 스와이프 모드 판정 + Swiper 초기화 =====
		 * data-swipe 가 있고 window.Swiper 가 로드돼 있을 때만 스와이프.
		 * 없으면 기본(flex) 모드로 자연 폴백. */
		const wantSwipe = root.hasAttribute("data-swipe");
		let swiper = null;

		if (wantSwipe && typeof window.Swiper === "function") {
			/* 버튼 너비: data-tab-width(px) 로 지정 가능. 없으면 CSS 값 사용.
			 * 예) <div class="indexed-tab-nav" data-swipe data-tab-width="80"> */
			const tabWidth = parseFloat(root.dataset.tabWidth);
			const hasTabWidth = Number.isFinite(tabWidth) && tabWidth > 0;

			/* Swiper 8 은 .swiper > .swiper-wrapper > .swiper-slide 구조 필요.
			 * 팀원 마크업(.tab-bar > .tab-bar__btn)을 런타임에 그 구조로 래핑. */
			const wrapper = document.createElement("div");
			wrapper.className = "swiper-wrapper";
			btns.forEach((btn) => {
				btn.classList.add("swiper-slide");
				/* flex 리셋: 기본 모드 CSS(.tab-bar__btn{flex:1})가 swiper의 flex-shrink:0 을
				 * 덮어써 슬라이드가 찌그러지는 것을 방지. !important 로 확정. */
				btn.style.setProperty("flex", "0 0 auto", "important");
				if (hasTabWidth) btn.style.setProperty("width", tabWidth + "px", "important"); // 버튼 고정폭 적용(!important 로 테마 CSS 우선)
				wrapper.appendChild(btn); // btn 을 wrapper 로 이동
			});
			bar.appendChild(wrapper);
			bar.classList.add("swiper");

			swiper = new window.Swiper(bar, {
				slidesPerView: "auto",
				centeredSlides: true, // 활성 탭을 가운데로
				centeredSlidesBounds: true, // 단, 양 끝(첫/마지막 탭)에선 여백 없이 붙임
				slideToClickedSlide: false, // 클릭 이동은 우리 로직이 담당
			});
		}

		/* 탭 클릭 스크롤 중에는 스크롤스파이 잠금
		 * (이동 중 중간 패널들이 스쳐 지나가며 active가 깜빡이는 것 방지) */
		let isClickScrolling = false;
		let clickedIndex = -1;

		/* 현재 버튼 active 갱신 + (스와이프 모드면) 활성 버튼 가운데 정렬 */
		const setActive = (index) => {
			btns.forEach((btn, i) => btn.classList.toggle("is-active", i === index));
			if (swiper) swiper.slideTo(index); // 활성 버튼을 가운데로 이동
		};

		let rafId = null; // smooth 도달 감시 rAF 핸들
		const releaseLock = () => {
			if (!isClickScrolling) return;
			isClickScrolling = false;
			if (rafId) {
				cancelAnimationFrame(rafId);
				rafId = null;
			}
			if (clickedIndex >= 0) {
				setActive(clickedIndex);
				clickedIndex = -1;
			}
		};

		/* 클릭 직후 짧은 유예: smooth 스크롤 잔여 이벤트를 사용자 스크롤로 오인해
		 * 가짜 해제되는 것을 방지 */
		let clickScrollAt = 0;
		const USER_SCROLL_GUARD = 400; // ms
		const releaseByUser = () => {
			if (Date.now() - clickScrollAt < USER_SCROLL_GUARD) return;
			releaseLock();
		};

		/* smooth 스크롤 목표 도달 감시 (시간이 아닌 위치 도달로 판정 → 기기/거리 무관) */
		const ARRIVE_EPS = 2; // 도달 판정 허용 오차(px)
		const ARRIVE_TIMEOUT = 1000; // 안전장치: 초과 시 강제 해제(ms)

		const waitArrival = (targetTop) => {
			if (rafId) cancelAnimationFrame(rafId);
			const startedAt = Date.now();
			const maxTop = document.documentElement.scrollHeight - window.innerHeight;
			/* 문서 끝을 넘는 좌표 요청 시 실제 도달값은 maxTop에서 멈추므로 클램프 */
			const goal = Math.max(0, Math.min(targetTop, maxTop));

			const step = () => {
				if (!isClickScrolling) {
					rafId = null;
					return;
				}
				const reached = Math.abs(window.pageYOffset - goal) <= ARRIVE_EPS;
				const timedOut = Date.now() - startedAt > ARRIVE_TIMEOUT;
				if (reached || timedOut) {
					rafId = null;
					releaseLock();
					return;
				}
				rafId = requestAnimationFrame(step);
			};
			rafId = requestAnimationFrame(step);
		};

		/* 버튼 클릭 → 대응 패널로 스크롤 (offset = 헤더 높이 + 고정 탭바 높이) */
		btns.forEach((btn, index) => {
			btn.addEventListener("click", (e) => {
				e.preventDefault();
				const panel = panels[index];
				if (!panel) return;

				isClickScrolling = true;
				clickScrollAt = Date.now();
				clickedIndex = index;
				setActive(index);

				const offset = getHeaderHeight() + bar.offsetHeight;
				const top = panel.getBoundingClientRect().top + window.pageYOffset - offset;

				window.scrollTo({ top, behavior: "smooth" });
				waitArrival(top);
			});
		});

		/* 사용자가 직접 스크롤(휠/터치)하면 락 즉시 해제 — 사용자 의도 우선 */
		window.addEventListener("wheel", releaseByUser, { passive: true });
		window.addEventListener("touchmove", releaseByUser, { passive: true });

		/* 고정 전 본래 탭바 높이(회전/리사이즈 대응 위해 비고정 상태에서 측정) */
		let barHeight = bar.offsetHeight;
		const lastPanel = panels[panels.length - 1];

		/* 탭바 fixed 전환 + placeholder 높이 유지 */
		const updateBarFixed = () => {
			const headerHeight = getHeaderHeight();
			const isFixed = bar.classList.contains("is-fixed");
			if (!isFixed) barHeight = bar.offsetHeight;
			const areaTop = area.getBoundingClientRect().top;

			/* 상단 경계: 탭바 영역 상단이 헤더 하단선에 닿음 = 고정 시작선
			 * 하단 경계: 마지막 패널 하단이 (헤더+탭바) 위로 올라감 = 패널 구간 끝
			 *   → 이 지점을 지나면 탭바 역할이 끝났으므로 고정 해제(다음 섹션 안 덮음) */
			const passedTop = areaTop <= headerHeight;
			const passedBottom = lastPanel.getBoundingClientRect().bottom <= headerHeight + barHeight;

			if (passedTop && !passedBottom) {
				if (!isFixed) {
					area.style.height = barHeight + "px"; // 자리 유지(점프 방지)
					bar.classList.add("is-fixed");
				}
				bar.style.top = headerHeight + "px"; // 헤더 바로 아래에 붙임
			} else if (isFixed) {
				bar.classList.remove("is-fixed");
				bar.style.top = "";
				area.style.height = "";
			}
		};

		/* 스크롤스파이: 임계선(헤더+탭바 하단) 넘은 마지막 패널을 active */
		const updateActiveTab = () => {
			if (isClickScrolling) return;
			const threshold = getHeaderHeight() + bar.offsetHeight + 10;
			let currentIndex = 0;
			panels.forEach((panel, i) => {
				if (panel.getBoundingClientRect().top <= threshold) currentIndex = i;
			});
			setActive(currentIndex);
		};

		/* 스크롤/리사이즈 (rAF 최적화) */
		let ticking = false;
		const onScroll = () => {
			if (ticking) return;
			requestAnimationFrame(() => {
				updateBarFixed();
				updateActiveTab();
				ticking = false;
			});
			ticking = true;
		};

		window.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", onScroll, { passive: true });
		updateBarFixed(); // 초기 1회
		updateActiveTab();
	};

	/* scope(document 또는 재렌더된 섹션) 안의 모든 .indexed-tab-nav 를 각각 초기화.
	 * scope 자신이 .indexed-tab-nav 인 경우(섹션 루트 = 컨테이너)도 포함. */
	const initAll = (scope) => {
		const roots = [...scope.querySelectorAll(".indexed-tab-nav")];
		if (scope.classList && scope.classList.contains("indexed-tab-nav")) {
			roots.unshift(scope);
		}
		roots.forEach(initTabNav);
	};

	/* 초기화 */
	const ready = (fn) => {
		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", fn);
		} else {
			fn();
		}
	};

	ready(() => initAll(document)); // 최초 1회: 전체 문서
	/* 커스터마이저/섹션 재렌더 대응(팀 컨벤션):
	 * 블록을 다시 그릴 때 그 섹션(e.target) 안에서만 재초기화 */
	document.addEventListener("shopify:section:load", (e) => initAll(e.target));
})();
